// src/api/settings.js
// API utility for provider key and settings management

function _withAuditVisibility(payload, response) {
  if (!payload || typeof payload !== "object") {
    return payload;
  }
  const auditVisibility = response?.headers?.get("x-tradepilot-audit-visibility");
  if (!auditVisibility) {
    return payload;
  }
  return { ...payload, __audit_visibility: String(auditVisibility).toLowerCase() };
}

// Provider Keys
export async function getProviderKeys() {
  const response = await fetch("/settings/provider-keys");
  if (!response.ok) throw new Error("Failed to fetch provider keys");
  return response.json();
}

export async function saveProviderKeys(keys) {
  const response = await fetch("/settings/provider-keys", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(keys),
  });
  if (!response.ok) throw new Error("Failed to save provider keys");
  return response.json();
}

export async function getProviderKeysStatus() {
  const response = await fetch("/settings/provider-keys-status");
  if (!response.ok) throw new Error("Failed to fetch provider key status");
  const payload = await response.json();
  return payload?.providers ?? {};
}

// General Settings
export async function getSettings() {
  const response = await fetch("/settings");
  if (!response.ok) throw new Error("Failed to fetch settings");
  const payload = await response.json();
  return payload?.settings ?? {};
}

export async function getAppSurfacePolicy() {
  const response = await fetch("/settings/app-surface-policy");
  if (!response.ok) throw new Error("Failed to fetch app surface policy");
  const payload = await response.json();
  return payload?.app_surface_policy ?? {};
}

export async function getLegalDisclaimerPolicy() {
  const response = await fetch("/legal/disclaimer");
  if (!response.ok) throw new Error("Failed to fetch legal disclaimer");
  const payload = await response.json();
  return {
    disclaimer_not_financial_advice:
      payload?.disclaimer ??
      "TradePilot is for informational purposes only and does not constitute financial advice.",
    legal_links: {
      terms_of_service: payload?.terms_url ?? "https://trytradepilot.com/terms",
      privacy_policy: payload?.privacy_url ?? "https://trytradepilot.com/privacy",
      risk_disclosure: payload?.risk_disclosure_url ?? "https://trytradepilot.com/risk-disclosure",
    },
    ops_baseline: {
      support_email: payload?.contact ?? "admin@trytradepilot.com",
      security_email: payload?.contact ?? "admin@trytradepilot.com",
    },
  };
}

export async function saveSettings(settings) {
  const response = await fetch("/settings", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(settings),
  });
  if (!response.ok) throw new Error("Failed to save settings");
  const payload = await response.json();
  return payload?.settings ?? {};
}

export async function getRiskPolicy() {
  const response = await fetch("/settings/risk-policy");
  if (!response.ok) throw new Error("Failed to fetch risk policy");
  const payload = await response.json();
  return payload?.risk_policy ?? {};
}

export async function saveRiskPolicy(policy) {
  const response = await fetch("/settings/risk-policy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(policy),
  });
  if (!response.ok) throw new Error("Failed to save risk policy");
  const payload = await response.json();
  return _withAuditVisibility(payload?.risk_policy ?? {}, response);
}

export async function getReadinessAlertPolicy() {
  const response = await fetch("/settings/readiness-alert-policy");
  if (!response.ok) throw new Error("Failed to fetch readiness alert policy");
  const payload = await response.json();
  return payload?.readiness_alert_policy ?? {};
}

export async function saveReadinessAlertPolicy(policy) {
  const response = await fetch("/settings/readiness-alert-policy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(policy),
  });
  if (!response.ok) throw new Error("Failed to save readiness alert policy");
  const payload = await response.json();
  return payload?.readiness_alert_policy ?? {};
}

export async function getBrokerSyncPolicy() {
  const response = await fetch("/settings/broker-sync-policy");
  if (!response.ok) throw new Error("Failed to fetch broker sync policy");
  const payload = await response.json();
  return payload?.broker_sync_policy ?? {};
}

export async function saveBrokerSyncPolicy(policy) {
  const response = await fetch("/settings/broker-sync-policy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(policy),
  });
  if (!response.ok) throw new Error("Failed to save broker sync policy");
  const payload = await response.json();
  return _withAuditVisibility(payload?.broker_sync_policy ?? {}, response);
}

export async function getReadinessActionPolicy() {
  const response = await fetch("/settings/readiness-action-policy");
  if (!response.ok) throw new Error("Failed to fetch readiness action policy");
  const payload = await response.json();
  return payload?.readiness_action_policy ?? {};
}

export async function saveReadinessActionPolicy(policy) {
  const response = await fetch("/settings/readiness-action-policy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(policy),
  });
  if (!response.ok) throw new Error("Failed to save readiness action policy");
  const payload = await response.json();
  return payload?.readiness_action_policy ?? {};
}

export async function getBrokerSyncStatus() {
  const response = await fetch("/portfolio/history/sync-broker/status");
  if (!response.ok) throw new Error("Failed to fetch broker sync status");
  const payload = await response.json();
  return payload?.sync ?? {};
}

export async function getPortfolioEquitySeedStatus() {
  const response = await fetch("/settings/portfolio-equity-seed-status");
  if (!response.ok) throw new Error("Failed to fetch portfolio equity seed status");
  const payload = await response.json();
  return payload?.portfolio_equity_seed ?? {};
}

export async function runBinancePreflight(options = {}) {
  const timeout = Number(options?.timeout ?? 6);
  const recvWindow = Number(options?.recv_window ?? 5000);
  const response = await fetch("/execution/binance/preflight", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ timeout, recv_window: recvWindow }),
  });
  if (!response.ok) throw new Error("Failed to run Binance preflight");
  return response.json();
}

export async function runAlpacaPreflight(options = {}) {
  const timeout = Number(options?.timeout ?? 6);
  const base_url = String(options?.base_url || "https://paper-api.alpaca.markets");
  const response = await fetch("/execution/alpaca/preflight", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ timeout, base_url }),
  });
  if (!response.ok) throw new Error("Failed to run Alpaca preflight");
  return response.json();
}

export async function runAlpacaDryRunOrder(order = {}) {
  const payload = {
    ticker: String(order?.ticker || "AAPL"),
    qty: Number(order?.qty ?? 1),
    side: String(order?.side || "buy"),
    order_type: String(order?.order_type || "market"),
    time_in_force: String(order?.time_in_force || "day"),
    timeout: Number(order?.timeout ?? 6),
    base_url: String(order?.base_url || "https://paper-api.alpaca.markets"),
  };
  const response = await fetch("/execution/alpaca/dry-run", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error("Failed to run Alpaca dry-run order");
  return response.json();
}

export async function armAlpacaLiveTrading(payload = {}) {
  const response = await fetch("/execution/alpaca/live-arm", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      passphrase: String(payload?.passphrase || ""),
      ttl_minutes: Number(payload?.ttl_minutes ?? 15),
    }),
  });
  if (!response.ok) throw new Error("Failed to arm Alpaca live trading");
  return response.json();
}

export async function getAlpacaLiveArmStatus() {
  const response = await fetch("/execution/alpaca/live-arm/status");
  if (!response.ok) throw new Error("Failed to fetch Alpaca live-arm status");
  return response.json();
}

export async function submitAlpacaGuardedOrder(order = {}) {
  const payload = {
    ticker: String(order?.ticker || "AAPL"),
    qty: Number(order?.qty ?? 1),
    side: String(order?.side || "buy"),
    order_type: String(order?.order_type || "market"),
    time_in_force: String(order?.time_in_force || "day"),
    timeout: Number(order?.timeout ?? 6),
    base_url: String(order?.base_url || "https://paper-api.alpaca.markets"),
    dry_run: Boolean(order?.dry_run ?? true),
    require_preflight: Boolean(order?.require_preflight ?? true),
  };
  const response = await fetch("/execution/alpaca/submit-guarded", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error("Failed to submit guarded Alpaca order");
  return response.json();
}

export async function planReinvestmentAllocations(payload) {
  const response = await fetch("/execution/reinvestment/plan", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
  if (!response.ok) throw new Error("Failed to build reinvestment plan");
  return response.json();
}

// Screener Presets
export async function getScreenerPresets() {
  const response = await fetch("/settings/screener-presets");
  if (!response.ok) throw new Error("Failed to fetch screener presets");
  const payload = await response.json();
  return payload?.presets ?? [];
}

export async function saveScreenerPresets(presets) {
  const response = await fetch("/settings/screener-presets", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ presets }),
  });
  if (!response.ok) throw new Error("Failed to save screener presets");
  const payload = await response.json();
  return payload?.presets ?? [];
}

export async function getScreenerUniversePolicy() {
  const response = await fetch("/settings/screener-universe");
  if (!response.ok) throw new Error("Failed to fetch screener universe policy");
  const payload = await response.json();
  return payload?.screener_universe ?? {};
}

export async function saveScreenerUniversePolicy(policy) {
  const response = await fetch("/settings/screener-universe", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(policy),
  });
  if (!response.ok) throw new Error("Failed to save screener universe policy");
  const payload = await response.json();
  return payload?.screener_universe ?? {};
}

export async function getScreenerViabilityPolicy() {
  const response = await fetch("/settings/screener-viability-policy");
  if (!response.ok) throw new Error("Failed to fetch screener viability policy");
  const payload = await response.json();
  return payload?.screener_viability_policy ?? {};
}

export async function saveScreenerViabilityPolicy(policy) {
  const response = await fetch("/settings/screener-viability-policy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(policy),
  });
  if (!response.ok) throw new Error("Failed to save screener viability policy");
  const payload = await response.json();
  return payload?.screener_viability_policy ?? {};
}

// Execution Policy
export async function getExecutionPolicy() {
  const response = await fetch("/settings/execution-policy");
  if (!response.ok) throw new Error("Failed to fetch execution policy");
  const payload = await response.json();
  return payload?.execution_policy ?? {};
}

export async function saveExecutionPolicy(policy) {
  const response = await fetch("/settings/execution-policy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(policy),
  });
  if (!response.ok) throw new Error("Failed to save execution policy");
  const payload = await response.json();
  return _withAuditVisibility(payload?.execution_policy ?? {}, response);
}

// Email Alert Policy
export async function getEmailAlertPolicy() {
  const response = await fetch("/settings/email-alert-policy");
  if (!response.ok) throw new Error("Failed to fetch email alert policy");
  const payload = await response.json();
  return payload?.email_alert_policy ?? {};
}

export async function saveEmailAlertPolicy(policy) {
  const response = await fetch("/settings/email-alert-policy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(policy),
  });
  if (!response.ok) throw new Error("Failed to save email alert policy");
  const payload = await response.json();
  return payload?.email_alert_policy ?? {};
}

// Rotation Policy
export async function getRotationPolicy() {
  const response = await fetch("/settings/rotation-policy");
  if (!response.ok) throw new Error("Failed to fetch rotation policy");
  const payload = await response.json();
  return payload?.rotation_policy ?? {};
}

export async function saveRotationPolicy(policy) {
  const response = await fetch("/settings/rotation-policy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(policy),
  });
  if (!response.ok) throw new Error("Failed to save rotation policy");
  const payload = await response.json();
  return payload?.rotation_policy ?? {};
}

export async function getProviderBudgetPolicy() {
  const response = await fetch("/settings/provider-budget-policy");
  if (!response.ok) throw new Error("Failed to fetch provider budget policy");
  const payload = await response.json();
  return payload?.provider_budget_policy ?? {};
}

export async function saveProviderBudgetPolicy(policy) {
  const response = await fetch("/settings/provider-budget-policy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(policy),
  });
  if (!response.ok) {
    let detail = "";
    try {
      const payload = await response.json();
      detail = typeof payload?.detail === "string" ? payload.detail : "";
    } catch {
      detail = "";
    }
    const error = new Error(
      detail
        ? `Provider budget policy validation failed: ${detail}`
        : "Failed to save provider budget policy"
    );
    error.status = response.status;
    error.detail = detail;
    error.source = "provider_budget_policy";
    throw error;
  }
  const payload = await response.json();
  return payload?.provider_budget_policy ?? {};
}

// Sleeve Policy
export async function getSleevePolicy() {
  const response = await fetch("/settings/sleeve-policy");
  if (!response.ok) throw new Error("Failed to fetch sleeve policy");
  const payload = await response.json();
  return payload?.sleeve_policy ?? {};
}

export async function saveSleevePolicy(policy) {
  const response = await fetch("/settings/sleeve-policy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(policy),
  });
  if (!response.ok) {
    let detail = "";
    try {
      const p = await response.json();
      detail = typeof p?.detail === "string" ? p.detail : "";
    } catch {
      detail = "";
    }
    const error = new Error(detail || "Failed to save sleeve policy");
    error.status = response.status;
    error.detail = detail;
    error.source = "sleeve_policy";
    throw error;
  }
  const payload = await response.json();
  return payload?.sleeve_policy ?? {};
}

// Autonomous Exec Policy (config fields only — arm/disarm use separate calls)
export async function getAutonomousExecPolicy() {
  const response = await fetch("/settings/autonomous-exec-policy");
  if (!response.ok) throw new Error("Failed to fetch autonomous exec policy");
  const payload = await response.json();
  return payload?.autonomous_exec_policy ?? {};
}

export async function saveAutonomousExecPolicy(policy) {
  const response = await fetch("/settings/autonomous-exec-policy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(policy),
  });
  if (!response.ok) throw new Error("Failed to save autonomous exec policy");
  const payload = await response.json();
  return _withAuditVisibility(payload?.autonomous_exec_policy ?? {}, response);
}

// Autonomous arm/disarm (governance-trail routes)
export async function armAutonomousEngine(reason) {
  const response = await fetch("/autonomous/arm", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason }),
  });
  if (!response.ok) {
    let detail = "";
    try {
      const p = await response.json();
      detail = typeof p?.detail === "string" ? p.detail : "";
    } catch {
      detail = "";
    }
    throw new Error(detail || "Failed to arm autonomous engine");
  }
  return response.json();
}

export async function disarmAutonomousEngine() {
  const response = await fetch("/autonomous/disarm", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  if (!response.ok) throw new Error("Failed to disarm autonomous engine");
  return response.json();
}

export async function getAutonomousStatus() {
  const response = await fetch("/autonomous/status");
  if (!response.ok) throw new Error("Failed to fetch autonomous status");
  return response.json();
}

export async function getAutonomousLog(options = {}) {
  const limit = Number(options?.limit ?? 5);
  const response = await fetch(`/autonomous/log?limit=${encodeURIComponent(limit)}`);
  if (!response.ok) throw new Error("Failed to fetch autonomous governance log");
  return response.json();
}

export async function exportAutonomousLogCsv(options = {}) {
  const limit = Number(options?.limit ?? 500);
  const response = await fetch(`/autonomous/log/export?limit=${encodeURIComponent(limit)}`);
  if (!response.ok) throw new Error("Failed to export autonomous governance log");
  return response.text();
}

